// src/views/admin/ADClasses.jsx
import { useState, useEffect } from "react";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";
import { useNavigate, useLocation } from "react-router-dom";
import { usePermissions } from "../../context/PermissionsContext";

// Composant Modal générique
function Modal({ title, onClose, children }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <div className="modal-title">
          {title}
          <button className="ibtn" style={{ border: "none" }} onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function ADClasses({ slug }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { canSee } = usePermissions();

  const [classes, setClasses] = useState([]);
  const [annees, setAnnees] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [filieres, setFilieres] = useState([]);
  const [enseignants, setEnseignants] = useState([]);
  const [matieres, setMatieres] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [anneeFilt, setAnneeFilt] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const [modal, setModal] = useState(null); // assign-ens | assign-mat | parents | archive | details
  const [selected, setSelected] = useState(null);
  const [pickedIds, setPickedIds] = useState([]);
  const [parents, setParents] = useState(null);

  const toast = useToastHook();
  const {
    getClasses, archiveClasse, restoreClasse,
    assignEnseignantsClasse, assignMatieresClasse,
    getAnneesScolaires, getUtilisateurs,
    getNiveaux, getFilieres, getMatieres, getParentsParClasse,
  } = useAdminService();

  // Navigation items (onglets) — chacun soumis à sa propre permission ;
  // "classes.voir" (contrôlé plus haut, au niveau de la sidebar) donne
  // seulement accès à cette page, pas à chaque sous-onglet individuellement.
  const navItems = [
    { path: "matieres", label: "MATIÈRES", icon: "ti-book", permission: "matieres.voir" },
    { path: "trimestres", label: "TRIMESTRES", icon: "ti-calendar-time", permission: "trimestres.voir" },
    { path: "plannifications", label: "ÉVALUATIONS", icon: "ti-clipboard-list", permission: "plannifications.voir" },
    { path: "notes", label: "SAISIE NOTES", icon: "ti-notebook", permission: "notes.voir" },
    { path: "bulletins", label: "BULLETINS", icon: "ti-file-certificate", permission: "bulletins.voir" },
    { path: "cartes", label: "CARTES SCOLAIRES", icon: "ti-id-badge-2", permission: "cartes.voir" },
  ];

  const isActive = (path) => {
    const currentPath = location.pathname.split('/').pop();
    return currentPath === path;
  };

  const getAdminPath = (path) => {
    return `/${slug}/admin/${path}`;
  };

  const navigateTo = (path) => {
    navigate(getAdminPath(path));
  };

  useEffect(() => {
    if (slug) init();
  }, [slug]);

  useEffect(() => {
    if (slug) { loadClasses(); loadMatieres(); }
  }, [slug, anneeFilt, showArchived]);

  const init = async () => {
    setLoading(true);
    try {
      const [anneesData, niveauxData, filieresData, ensData] = await Promise.all([
        getAnneesScolaires(slug, { show_archived: "false" }),
        getNiveaux(slug),
        getFilieres(slug),
        getUtilisateurs(slug, { role: "ENSEIGNANT", page_size: "all" }),
      ]);
      setAnnees(anneesData);
      setNiveaux(niveauxData);
      setFilieres(filieresData);
      setEnseignants(ensData.results || ensData);

      const anneeActive = anneesData.find(a => a.actif);
      setAnneeFilt(String((anneeActive || anneesData[0])?.id || ""));
    } catch (error) {
      toast.error("Impossible de charger les données de référence");
    } finally {
      setLoading(false);
    }
  };

  const loadClasses = async () => {
    try {
      const params = { show_archived: showArchived ? "true" : "false" };
      if (anneeFilt) params.annee_scolaire = anneeFilt;
      const data = await getClasses(slug, params);
      setClasses(data);
    } catch (error) {
      toast.error("Impossible de charger les classes");
    }
  };

  const loadMatieres = async () => {
    if (!anneeFilt) return;
    try {
      const data = await getMatieres(slug, { annee_scolaire: anneeFilt });
      setMatieres(data);
    } catch (error) {
      toast.error("Impossible de charger les matières");
    }
  };

  const filtered = classes.filter(c => c.nom?.toLowerCase().includes(search.toLowerCase()));

  const anneeActive = annees.find(a => a.actif);

  const openDetails = (c) => {
    setSelected(c);
    setModal("details");
  };

  const openArchive = (c) => { setSelected(c); setModal("archive"); };
  const handleArchive = async () => {
    setSaving(true);
    try {
      await archiveClasse(slug, selected.id);
      setClasses(prev => prev.map(c => c.id === selected.id ? { ...c, archived: true } : c));
      toast.success(`Classe "${selected.nom}" archivée avec succès`);
      setModal(null);
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible d'archiver cette classe");
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (c) => {
    try {
      await restoreClasse(slug, c.id);
      setClasses(prev => prev.map(x => x.id === c.id ? { ...x, archived: false } : x));
      toast.success(`Classe "${c.nom}" restaurée avec succès`);
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible de restaurer cette classe");
    }
  };

  const openAssignEns = (c) => {
    setSelected(c);
    setPickedIds((c.enseignants_detail || []).map(e => e.id));
    setModal("assign-ens");
  };
  const openAssignMat = (c) => {
    setSelected(c);
    setPickedIds((c.matieres_detail || []).map(m => m.id));
    setModal("assign-mat");
  };

  const togglePicked = (id) => {
    setPickedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleAssign = async () => {
    setSaving(true);
    try {
      if (modal === "assign-ens") {
        const updated = await assignEnseignantsClasse(slug, selected.id, { enseignants: pickedIds, mode: "replace" });
        setClasses(prev => prev.map(c => c.id === selected.id ? updated : c));
        toast.success("Enseignants mis à jour avec succès");
      } else {
        const updated = await assignMatieresClasse(slug, selected.id, { matieres: pickedIds, mode: "replace" });
        setClasses(prev => prev.map(c => c.id === selected.id ? updated : c));
        toast.success("Matières mises à jour avec succès");
      }
      setModal(null);
    } catch (error) {
      toast.error("Une erreur est survenue");
    } finally {
      setSaving(false);
    }
  };

  const openParents = async (c) => {
    setSelected(c);
    setModal("parents");
    setParents(null);
    try {
      const data = await getParentsParClasse(slug, c.id);
      setParents(data);
    } catch (error) {
      toast.error("Impossible de charger les parents");
      setParents([]);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      {/* Barre de navigation des onglets — filtrée par permission */}
      <div className="tab-nav">
        {navItems.filter(item => canSee(item.permission)).map(({ path, label, icon }) => (
          <button
            key={path}
            className={`tab-btn ${isActive(path) ? 'active' : ''}`}
            onClick={() => navigateTo(path)}
          >
            <i className={`ti ${icon}`} />
            {label}
          </button>
        ))}
      </div>

      {/* Alerte si pas d'année active */}
      {!anneeActive && (
        <div className="alert-banner">
          <i className="ti ti-alert-triangle" />
          <span>Aucune année scolaire active. Veuillez activer une année scolaire.</span>
          <button className="bprim" onClick={() => navigateTo("annees-scolaires")}>
            <i className="ti ti-calendar" /> Activer une année
          </button>
        </div>
      )}

      {/* Barre d'outils — page de visualisation : la création et la
          modification des classes se font désormais dans Paramètres → Classes */}
      <div className="sb2">
        <input
          type="text"
          placeholder="Rechercher une classe…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select value={anneeFilt} onChange={e => setAnneeFilt(e.target.value)}>
          {annees.map(a => (
            <option key={a.id} value={a.id}>{a.nom}{a.actif ? " (active)" : ""}</option>
          ))}
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
          Voir archivées
        </label>
        <button className="bsec" onClick={loadClasses}><i className="ti ti-refresh" />Actualiser</button>
      </div>

      {/* Tableau des classes */}
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Classe</th>
              <th>Niveau</th>
              <th>Séries</th>
              <th>Enseignants</th>
              <th>Matières</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => (
              <tr key={c.id}>
                <td style={{ fontWeight: 500 }}>
                  {c.nom}
                  {c.archived && <span className="pill warn" style={{ marginLeft: 8 }}>Archivée</span>}
                </td>
                <td>{c.niveau_nom || "—"}</td>
                <td>{c.filiere_nom || "—"}</td>
                <td>{c.enseignants_detail?.length || 0}</td>
                <td>{c.matieres_detail?.length || 0}</td>
                <td>
                  <span className={`pill ${c.archived ? "warn" : "ok"}`}>{c.archived ? "Archivée" : "Active"}</span>
                </td>
                <td>
                  <div className="act">
                    {/* Détails */}
                    <button
                      title="Détails"
                      onClick={() => openDetails(c)}
                      style={{ background: "#17a2b8", color: "white" }}
                      onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                    >
                      <i className="ti ti-info-circle" />
                    </button>

                    {/* Enseignants */}
                    {canSee("classes.assigner_enseignants") && (
                      <button
                        title="Enseignants"
                        onClick={() => openAssignEns(c)}
                        style={{ background: "#28a745", color: "white" }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                      >
                        <i className="ti ti-users" />
                      </button>
                    )}

                    {/* Matières */}
                    {canSee("classes.assigner_matieres") && (
                      <button
                        title="Matières"
                        onClick={() => openAssignMat(c)}
                        style={{ background: "#6f42c1", color: "white" }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                      >
                        <i className="ti ti-book" />
                      </button>
                    )}

                    {/* Parents */}
                    {canSee("classes.voir_parents") && (
                      <button
                        title="Parents"
                        onClick={() => openParents(c)}
                        style={{ background: "#fd7e14", color: "white" }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                      >
                        <i className="ti ti-address-book" />
                      </button>
                    )}

                    {/* Archiver / Restaurer — désactivé volontairement pour l'instant.
                        Réactiver en décommentant ce bloc si le besoin revient ; le check
                        canSee("classes.archiver") est déjà en place pour ce moment-là. */}
                    {/*
                    {!c.archived ? (
                      canSee("classes.archiver") && (
                        <button
                          title="Archiver"
                          onClick={() => openArchive(c)}
                          style={{ background: "var(--pr)", color: "white" }}
                          onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                          onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                        >
                          <i className="ti ti-archive" />
                        </button>
                      )
                    ) : (
                      canSee("classes.archiver") && (
                        <button
                          title="Restaurer"
                          onClick={() => handleRestore(c)}
                          style={{ background: "#20c997", color: "white" }}
                          onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                          onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                        >
                          <i className="ti ti-restore" />
                        </button>
                      )
                    )}
                    */}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan="7" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>Aucune classe trouvée</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Détails */}
      {modal === "details" && selected && (
        <Modal title={`Détails de la classe ${selected.nom}`} onClose={() => setModal(null)}>
          {/* Enseignants */}
          <div style={{ marginBottom: 20 }}>
            <h4 style={{ fontSize: 15, fontWeight: 600, color: "var(--t)", marginBottom: 10 }}>
              <i className="ti ti-users" style={{ marginRight: 6 }} /> Enseignants assignés
            </h4>
            {selected.enseignants_detail?.length > 0 ? (
              selected.enseignants_detail.map(e => (
                <div key={e.id} className="avc" style={{ padding: 6, borderBottom: "0.5px solid var(--b)" }}>
                  <div className="av pu" style={{ width: 20, height: 20, fontSize: 10 }}>E</div>
                  <span>{e.username}{e.nom ? ` (${e.prenom} ${e.nom})` : ""}</span>
                </div>
              ))
            ) : (
              <div style={{ color: "var(--h)", fontSize: 13 }}>Aucun enseignant assigné</div>
            )}
          </div>

          {/* Matières */}
          <div>
            <h4 style={{ fontSize: 15, fontWeight: 600, color: "var(--t)", marginBottom: 10 }}>
              <i className="ti ti-book" style={{ marginRight: 6 }} /> Matières assignées
            </h4>
            {selected.matieres_detail?.length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {selected.matieres_detail.map(m => (
                  <span key={m.id} className="pill info">{m.nom}</span>
                ))}
              </div>
            ) : (
              <div style={{ color: "var(--h)", fontSize: 13 }}>Aucune matière assignée</div>
            )}
          </div>
        </Modal>
      )}

      {/* Modal Archive */}
      {modal === "archive" && selected && (
        <Modal title="Archiver la classe" onClose={() => setModal(null)}>
          <div style={{ fontSize: 14, color: "var(--m)" }}>
            Archiver <strong>{selected.nom}</strong> ?
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleArchive} disabled={saving}>
              {saving ? "..." : "Archiver"}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal Assigner Enseignants/Matières */}
      {(modal === "assign-ens" || modal === "assign-mat") && selected && (
        <Modal title={modal === "assign-ens" ? `Enseignants — ${selected.nom}` : `Matières — ${selected.nom}`} onClose={() => setModal(null)}>
          <div style={{ maxHeight: 300, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
            {(modal === "assign-ens" ? enseignants : matieres).map(item => (
              <label key={item.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
                <input type="checkbox" checked={pickedIds.includes(item.id)} onChange={() => togglePicked(item.id)} />
                {modal === "assign-ens" ? `${item.first_name} ${item.last_name}` : item.nom}
              </label>
            ))}
            {(modal === "assign-ens" ? enseignants : matieres).length === 0 && (
              <div style={{ color: "var(--h)", fontSize: 13 }}>
                {modal === "assign-ens" ? "Aucun enseignant trouvé." : "Aucune matière pour cette année scolaire."}
              </div>
            )}
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleAssign} disabled={saving}>
              {saving ? "..." : "Enregistrer"}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal Parents */}
      {modal === "parents" && selected && (
        <Modal title={`Parents — ${selected.nom}`} onClose={() => setModal(null)}>
          {!parents ? <Loader /> : parents.length === 0 ? (
            <div style={{ color: "var(--h)" }}>Aucun parent trouvé pour cette classe.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {parents.map(p => (
                <div key={`${p.parent_id}-${p.enfant.id}`} className="card" style={{ padding: 10 }}>
                  <div style={{ fontWeight: 500 }}>{p.prenom_parent} {p.nom_parent}</div>
                  <div style={{ fontSize: 12, color: "var(--h)" }}>Enfant : {p.enfant.prenom} {p.enfant.nom}</div>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}

      {/* Styles */}
      <style>{`
        /* Barre de navigation des onglets (thème bleu + or) */
        .tab-nav {
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
          background: linear-gradient(180deg, var(--pr-dk) 0%, var(--pr) 100%);
          border-radius: 10px;
          border-bottom: 3px solid var(--or);
          padding: 8px 12px;
          margin-bottom: 16px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .tab-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 18px;
          border: none;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          text-transform: uppercase;
          cursor: pointer;
          transition: all 0.3s ease;
          background: transparent;
          color: rgba(255,255,255,0.75);
          font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }

        .tab-btn i {
          font-size: 18px;
        }

        .tab-btn:hover {
          background: rgba(255,255,255,0.12);
          color: var(--or-lt);
        }

        .tab-btn.active {
          background: white;
          color: var(--pr);
          box-shadow: inset 0 -3px 0 var(--or), 0 2px 8px rgba(0,0,0,0.15);
        }

        .tab-btn.active i {
          color: var(--or);
        }

        .tab-btn.active:hover {
          background: white;
          color: var(--pr);
        }

        /* Alerte */
        .alert-banner {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 18px;
          background: var(--am-bg);
          border: 2px solid var(--am);
          border-radius: 8px;
          color: var(--am);
          font-size: 14px;
          margin-bottom: 14px;
          flex-wrap: wrap;
        }

        .alert-banner i {
          font-size: 22px;
          flex-shrink: 0;
        }

        .alert-banner span {
          flex: 1;
        }

        .alert-banner .bprim {
          background: var(--am);
          flex-shrink: 0;
        }

        .alert-banner .bprim:hover {
          opacity: 0.85;
        }

        /* Pill pour archivée dans le tableau */
        .pill.warn {
          background: var(--am-bg);
          color: var(--am);
        }

        /* Styles des boutons d'action */
        .act button {
          width: 30px;
          height: 30px;
          border-radius: 4px;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          transition: all .2s;
        }

        .act button:hover {
          opacity: 0.8 !important;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .tab-nav {
            padding: 6px 8px;
            gap: 2px;
          }

          .tab-btn {
            padding: 6px 10px;
            font-size: 11px;
          }

          .tab-btn i {
            font-size: 14px;
          }
        }

        @media (max-width: 576px) {
          .tab-btn span {
            display: none;
          }

          .tab-btn i {
            font-size: 18px;
            margin: 0;
          }
        }
      `}</style>
    </div>
  );
}